import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, LinkBox, LinkOverlay } from '@chakra-ui/react';

import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import {
  type BackToSchool2026Phase2Variant,
  BTS_2026_P2_VARIANTS,
  getBts2026Phase2CheckoutHref,
} from '~/features/promotions/constants/back-to-school-2026-phase-2-sale';

export type BackToSchool2026Phase2PromoBannerProps = {
  variant: BackToSchool2026Phase2Variant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  /** Eager-load the art when the banner is the first/visible carousel slide. */
  imagePriority?: boolean;
  onCtaClick?: () => void;
};

function BackToSchool2026Phase2BannerBackground({
  variant,
  imagePriority,
}: {
  variant: BackToSchool2026Phase2Variant;
  imagePriority?: boolean;
}) {
  const config = BTS_2026_P2_VARIANTS[variant];

  return (
    <Box inset={0} pointerEvents='none' position='absolute'>
      <Box bottom={0} display={{ base: 'block', md: 'none' }} left={0} position='absolute' right={0} top={0}>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={config.catalogBgMobile}
          style={{ objectFit: 'cover', objectPosition: 'bottom right', pointerEvents: 'none' }}
          fill
        />
      </Box>
      <Box bottom={0} display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='656px'
          src={config.catalogBg}
          // Anchor right: the pencil art lives at the asset's right edge, and `cover` crops
          // horizontally whenever the slide stretches taller than the asset's aspect ratio.
          style={{ objectFit: 'cover', objectPosition: 'right', pointerEvents: 'none' }}
          fill
        />
      </Box>
    </Box>
  );
}

/**
 * Catalog hero carousel banner for Back to School 2026 phase 2 (Figma 1944:1212 G1 desktop,
 * 1944:1182 G1 mobile, 1944:1227 G2 desktop, 1944:1197 G2 mobile). Line 2 is desktop-only, and
 * line 1 wraps after the colon on mobile so the offer sits on its own line. The full sentence
 * still reaches assistive tech through the overlay's `aria-label`.
 */
export function BackToSchool2026Phase2PromoBanner({
  variant,
  promoCode,
  imagePriority,
  onCtaClick,
}: BackToSchool2026Phase2PromoBannerProps) {
  const config = BTS_2026_P2_VARIANTS[variant];
  const ariaLabel = `${config.catalogHeadlineLine1} ${config.catalogHeadlineLine2}: ${config.catalogCtaLabel}`;
  // Mobile wraps line 1 after the colon so the offer ("50% OFF" / "sitewide") gets its own line.
  const [line1Lead, line1Offer] = config.catalogHeadlineLine1.split(': ');

  return (
    <LinkBox
      background={{ base: config.catalogBackgroundGradientMobile, md: config.catalogBackgroundGradient }}
      border='1px solid'
      borderColor={config.catalogBorderColor}
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: 'auto', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <BackToSchool2026Phase2BannerBackground imagePriority={imagePriority} variant={variant} />
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexDirection={{ base: 'column', md: 'row' }}
        // No desktop gap: the design butts the headline box against the CTA (Figma 1944:1212) and
        // separates them with the headline's leftover space. Padding is trimmed from the design's
        // 32px to keep line 2 on one line with breathing room before the CTA.
        gap={{ base: 3, md: 0 }}
        height='100%'
        justifyContent={{ base: 'center', md: 'space-between' }}
        minHeight={{ base: 'auto', md: '120px' }}
        padding={{ base: '16px', md: '24px' }}
        position='relative'
        zIndex={1}
      >
        <Heading
          as='h3'
          display='flex'
          flexDirection='column'
          flexGrow={1}
          fontSize={{ base: '1.375rem', md: '1.25rem' }}
          fontWeight='600'
          letterSpacing='-0.01em'
          lineHeight={1.2}
          minWidth={0}
        >
          <Box as='span' color='white'>
            {line1Offer ? `${line1Lead}: ` : config.catalogHeadlineLine1}
            {line1Offer ? (
              <Box as='span' display={{ base: 'block', md: 'inline' }}>
                {line1Offer}
              </Box>
            ) : null}
          </Box>
          <Box as='span' color={config.catalogHeadlineLine2Color} display={{ base: 'none', md: 'inline' }}>
            {config.catalogHeadlineLine2}
          </Box>
        </Heading>
        <Button
          alignItems='center'
          as='span'
          backgroundColor={config.catalogCtaColor}
          borderRadius='4px'
          color={config.catalogCtaTextColor}
          display='inline-flex'
          flexShrink={0}
          fontSize='12px'
          fontWeight='400'
          height='auto'
          leftIcon={<CircleArrowRightIcon boxSize='14px' color={config.catalogCtaTextColor} aria-hidden />}
          lineHeight={1.4}
          paddingLeft='12px'
          paddingRight='16px'
          paddingY='10px'
          pointerEvents='none'
          tabIndex={-1}
          variant='unstyled'
          aria-hidden
        >
          {config.catalogCtaLabel}
        </Button>
      </Flex>
      <LinkOverlay
        aria-label={ariaLabel}
        as={NextLink}
        href={getBts2026Phase2CheckoutHref(variant, promoCode)}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onCtaClick}
      />
    </LinkBox>
  );
}
